import React, { useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import analytics from 'storefront-analytics/dist/analytics';
import MicroInteraction, { ApiMicroInteraction } from '@chewy/pet-profile-micro-interaction';
import { AnalyticsData } from '@chewy/pet-profile-micro-interaction/dist/esm/packages/micro-interaction/src/types';
import { KibButtonNew } from '@chewy/kib-controls-react';
import { KibInputText, KibInputSelect } from '@chewy/kib-fields-react';
import PageLoader from '../components/loaders/PageLoader';
import useLocationQuery from '../hooks/useLocationQuery';
import { getMicroInteraction, getMintRequestParam } from '../api/mintApi';
import packageLockJson from '../../../package-lock.json';
import '@chewy/pet-profile-micro-interaction/dist/esm/main.css';
import './mint-page.scss';
import {
  GetMicroInteractionClientPageEnum,
  GetMicroInteractionClientPlatformTypeEnum,
  GetMicroInteractionRequest,
} from '../api/generated-client/MicroInteraction-Service.v1';

export const getMintVersion = (): string => {
  const microInteractionVersion =
    packageLockJson.dependencies['@chewy/pet-profile-micro-interaction'];
  return microInteractionVersion.version;
};

const getClientPageEnum = (clientPage: string | null): GetMicroInteractionClientPageEnum => {
  switch (clientPage) {
    case 'account':
      return GetMicroInteractionClientPageEnum.Account;
    case 'typ':
      return GetMicroInteractionClientPageEnum.Typ;
    case 'home':
      return GetMicroInteractionClientPageEnum.Home;
    default:
      return GetMicroInteractionClientPageEnum.PetProfileView;
  }
};

const clientPageOptions: string[] = ['account', 'home', 'pet_profile_view', 'typ'];

const parseId = (id?: string): number | null => {
  const parsed = id ? Number.parseInt(id) : null;
  if (parsed === null || Number.isNaN(parseId)) {
    return null;
  }
  return parsed;
};

const getSegmentLogOutputElement = () => document.querySelector('#segment-logs');

const getAPIQueryParams = (queryParams: GetMicroInteractionRequest) => {
  const queries = [];
  queries.push({ key: 'client_page', value: queryParams.clientPage });
  queries.push({ key: 'client_platform_type', value: queryParams.clientPlatformType });
  queries.push({ key: 'id', value: queryParams.id });
  queries.push({ key: 'pet_id', value: queryParams.petId });
  queries.push({ key: 'client_platform_version', value: queryParams.clientPlatformVersion });

  return queries.reduce((acc, keyValue, index) => {
    const isLastEntry = index === queries.length - 1;
    if (keyValue.value) {
      acc = `${acc}${keyValue.key}=${keyValue.value}`;
      acc = isLastEntry ? `${acc}` : `${acc}&`;
    }
    return acc;
  }, '?');
};

export default function MintPage() {
  const query = useLocationQuery();
  const queryValueMintId = query.get('mi_id');
  const queryValuePetProfileId = query.get('pet_profile_id');
  const queryValueClientPage = query.get('client_page');
  const [petProfileId, setPetProfileId] = useState<number | null>(
    queryValuePetProfileId ? parseId(queryValuePetProfileId) : null,
  );
  const [mintIdParsed, setMintIdParsed] = useState<number | null>(
    queryValueMintId ? parseId(queryValueMintId) : null,
  );
  const [selectedClientPage, setSelectedClientPage] = useState<GetMicroInteractionClientPageEnum>(
    getClientPageEnum(queryValueClientPage),
  );

  const mintQueryParams = getMintRequestParam(
    mintIdParsed || null,
    getMintVersion(),
    selectedClientPage,
    GetMicroInteractionClientPlatformTypeEnum.Web,
    petProfileId ? petProfileId : null,
  );

  const [queryParams, setQueryParams] = useState(getAPIQueryParams(mintQueryParams));

  const { isFetching: mintFetching, data: mintData, refetch } = useQuery(
    ['mint', mintQueryParams],
    getMicroInteraction,
    { enabled: false, cacheTime: 0 },
  );

  const reloadMint = () => {
    const logElement = getSegmentLogOutputElement();
    if (logElement) {
      logElement.innerHTML = '';
    }
    setQueryParams(getAPIQueryParams(mintQueryParams));
    refetch();
  };

  useEffect(() => {
    reloadMint();
  }, []);

  return (
    <>
      <div className="testing-page">
        <div className="testing-page__inputs">
          <KibInputText
            label="Pet Profile ID"
            onChange={(event) => setPetProfileId(parseId(event.target.value))}
            value={petProfileId || ''}
          />
          <KibInputText
            label="MINT ID"
            onChange={(event) => setMintIdParsed(parseId(event.target.value))}
            value={mintIdParsed || ''}
          />
          <KibInputSelect
            label="Client Page"
            options={clientPageOptions}
            value={selectedClientPage}
            onChange={(event) => {
              setSelectedClientPage(getClientPageEnum(event.target.value));
            }}
          />
          <KibButtonNew onClick={() => reloadMint()}>Apply</KibButtonNew>
        </div>
      </div>
      {mintFetching && <PageLoader />}
      {mintData && (
        <MicroInteraction
          miData={mintData as ApiMicroInteraction}
          analyticsClient={{
            segmentTrack: (event: string, data: AnalyticsData) => {
              const noOfEvents =
                (getSegmentLogOutputElement()?.innerHTML.match(/\n/g)?.length || 0) + 1;
              document
                .querySelector('#segment-logs')
                ?.append(`\n[${noOfEvents}] ${event} ${JSON.stringify(data)}`);
              analytics.segmentTrack(event, data);
            },
          }}
        />
      )}

      <div className="mint-page">
        <p>MINT Version : {getMintVersion()}</p>
        <p>Segment Logs from Mint Component:</p>
        <pre id="segment-logs" />
        <p>API Response:</p>
        <a
          target="_blank"
          rel="noreferrer"
          href={`/api/petprofile/v1/mi${queryParams}`}
        >{`GET /api/petprofile/v1/mi${queryParams}`}</a>
        <pre>{JSON.stringify(mintData, null, 4)}</pre>
      </div>
    </>
  );
}
